import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getReviewItems,
  getReviewStats,
  removeReviewItem,
  clearReviewQueue,
} from '../lib/reviewQueue';
import { describeTone } from '../lib/toneUtils';

/**
 * ReviewPage —— 错音复习
 *
 * Midoo 教学法的第四步：把练错的音沉淀下来，下次优先重练。
 *
 * 数据完全来自本地（localStorage），因此这一页**不依赖任何外部服务**，
 * 断网、密钥失效都能用。
 */
export default function ReviewPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ total: 0, characters: 0 });

  const reload = useCallback(() => {
    setItems(getReviewItems(100));
    setStats(getReviewStats());
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  /** 拿这条错音的原始句子去重练 */
  const handlePractice = (item) => {
    if (!item.cantonese) {
      // 没有原句时，退化成只练这一个字
      navigate('/practice', {
        state: {
          story: {
            title: `复习：${item.character}`,
            cantonese: item.character,
            jyutping: item.jyutping || '',
            mandarin: '',
          },
        },
      });
      return;
    }

    navigate('/practice', {
      state: {
        story: {
          title: '错音复习',
          cantonese: item.cantonese,
          jyutping: item.jyutping || '',
          mandarin: '',
        },
      },
    });
  };

  const handleRemove = (key, e) => {
    e.stopPropagation();
    removeReviewItem(key);
    reload();
  };

  const handleClear = () => {
    if (!confirm('确定清空所有错音记录？此操作不可撤销。')) return;
    clearReviewQueue();
    reload();
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-28 dark:bg-zinc-900">
      {/* Header */}
      <header className="bg-gradient-to-br from-rose-500 to-amber-500 px-6 pt-12 pb-6 dark:from-rose-700 dark:to-amber-700">
        <div className="mb-3 flex items-center justify-between">
          <button type="button" onClick={() => navigate(-1)} className="text-white" aria-label="返回">
            <span className="material-icons !text-2xl">arrow_back</span>
          </button>
          {stats.total > 0 && (
            <button
              type="button"
              onClick={handleClear}
              className="rounded-full bg-white/20 px-3 py-1 text-[11px] font-bold text-white active:opacity-70"
            >
              清空
            </button>
          )}
        </div>
        <h1 className="text-2xl font-bold text-white">错音复习</h1>
        <p className="mt-1 text-sm text-white/85">
          {stats.total > 0
            ? `有 ${stats.characters} 个字读错过，共 ${stats.total} 条记录`
            : '练错的字会自动收进这里'}
        </p>
      </header>

      <main className="px-5 py-5">
        {items.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-16 text-center">
            <span className="material-icons !text-5xl text-emerald-300">verified</span>
            <p className="text-sm font-bold text-zinc-600 dark:text-zinc-300">还没有错音记录</p>
            <p className="max-w-[260px] text-xs leading-relaxed text-zinc-400 dark:text-zinc-500">
              去课程里做几次跟读练习，读错声调的字会自动出现在这里，
              方便你集中重练。
            </p>
            <button
              type="button"
              onClick={() => navigate('/')}
              className="mt-2 rounded-xl bg-teal-500 px-5 py-2.5 text-sm font-bold text-white active:opacity-80"
            >
              去看课程
            </button>
          </div>
        ) : (
          <>
            <div className="mb-3 flex items-center gap-1.5 rounded-xl bg-amber-50 px-3 py-2 dark:bg-amber-900/30">
              <span className="material-icons !text-base text-amber-600 dark:text-amber-400">info</span>
              <p className="text-[11px] leading-relaxed text-amber-700 dark:text-amber-300">
                点任意一条可以直接重练。连续读对两次后会自动移出列表。
              </p>
            </div>

            <div className="space-y-2.5">
              {items.map((item) => (
                <div
                  key={item.key}
                  role="button"
                  tabIndex={0}
                  onClick={() => handlePractice(item)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') handlePractice(item);
                  }}
                  className="flex items-center gap-4 rounded-2xl border border-zinc-100 bg-white p-4 text-left shadow-sm transition-transform active:scale-[0.98] dark:border-zinc-700/60 dark:bg-zinc-800"
                >
                  {/* 汉字 */}
                  <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-rose-50 text-2xl font-bold text-rose-600 dark:bg-rose-900/40 dark:text-rose-300">
                    {item.character}
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold text-zinc-800 dark:text-zinc-100">
                        应为 {describeTone(item.expectedTone) || '—'}
                      </span>
                      {item.actualTone != null && item.actualTone !== item.expectedTone && (
                        <span className="text-xs text-rose-500 dark:text-rose-400">
                          读成了 {describeTone(item.actualTone) || item.actualTone}
                        </span>
                      )}
                    </div>

                    <div className="mt-1 flex items-center gap-3 text-[11px] text-zinc-400 dark:text-zinc-500">
                      <span>错 {item.count} 次</span>
                      {typeof item.lastAccuracy === 'number' && (
                        <span>上次 {Math.round(item.lastAccuracy)} 分</span>
                      )}
                      {item.jyutping && (
                        <span className="truncate font-mono">{item.jyutping}</span>
                      )}
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={(e) => handleRemove(item.key, e)}
                    aria-label="移出复习列表"
                    className="shrink-0 rounded-full p-1.5 text-zinc-300 active:bg-zinc-100 dark:text-zinc-600 dark:active:bg-zinc-700"
                  >
                    <span className="material-icons !text-lg">close</span>
                  </button>
                </div>
              ))}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
